import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bell, CheckCheck, Info, Package } from "lucide-react";
import { cn } from "@/lib/cn";
import { useActiveOrg } from "@/features/tenancy/hooks";
import {
  fetchNotifications,
  markRead,
  subscribeToNotifications,
  type Notification,
} from "./api";

const ICONOS: Record<string, typeof Bell> = {
  "order.created": Package,
  "payment.pending_verification": AlertTriangle,
};

const TONOS: Record<string, string> = {
  success: "text-ok",
  warning: "text-warn",
  critical: "text-crit",
  info: "text-ink-2",
};

function haceCuanto(iso: string): string {
  const minutos = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return "ahora";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short" });
}

export function NotificationBell() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  const organizationId = org?.organizationId;

  const avisos = useQuery({
    queryKey: ["notifications", organizationId],
    queryFn: () => fetchNotifications(organizationId!),
    enabled: Boolean(organizationId),
    // La suscripcion trae lo nuevo; esto es la red por si se cae el socket.
    refetchInterval: 120_000,
  });

  // Un pedido que entra mientras el panel esta abierto tiene que aparecer sin
  // que nadie recargue: es la mitad del sentido de tener campana.
  useEffect(() => {
    if (!organizationId) return;
    return subscribeToNotifications(organizationId, () => {
      void qc.invalidateQueries({ queryKey: ["notifications", organizationId] });
      // El pedido nuevo tambien cambia la lista y el tablero.
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    });
  }, [organizationId, qc]);

  // Cerrar al pulsar fuera. Sin esto el panel se queda abierto tapando la
  // pantalla mientras se trabaja.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const lista = avisos.data ?? [];
  const sinLeer = lista.filter((n) => !n.read_at);

  async function abrirAviso(aviso: Notification) {
    setAbierto(false);
    if (!aviso.read_at) {
      await markRead([aviso.id]).catch(() => {});
      void qc.invalidateQueries({ queryKey: ["notifications", organizationId] });
    }
    if (aviso.action_url) navigate(aviso.action_url);
  }

  async function marcarTodo() {
    await markRead(sinLeer.map((n) => n.id)).catch(() => {});
    void qc.invalidateQueries({ queryKey: ["notifications", organizationId] });
  }

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="relative grid size-8 place-items-center rounded-md text-ink-2 hover:bg-sunk"
        aria-label={
          sinLeer.length > 0 ? `${sinLeer.length} avisos sin leer` : "Notificaciones"
        }
      >
        <Bell className="size-4" strokeWidth={1.8} />
        {sinLeer.length > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-[15px] place-items-center rounded-full bg-crit px-1 text-mark font-bold leading-[15px] text-white">
            {sinLeer.length > 9 ? "9+" : sinLeer.length}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 top-10 z-30 w-[336px] overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          <header className="flex items-center justify-between border-b border-line-2 px-3.5 py-2.5">
            <h2 className="text-ui font-semibold">Avisos</h2>
            {sinLeer.length > 0 && (
              <button
                type="button"
                onClick={() => void marcarTodo()}
                className="flex items-center gap-1 text-meta text-ink-3 hover:text-ink"
              >
                <CheckCheck className="size-3.5" />
                Marcar todo
              </button>
            )}
          </header>

          <div className="max-h-[380px] overflow-y-auto">
            {avisos.isPending ? (
              <p className="px-3.5 py-6 text-center text-meta text-ink-3">Cargando…</p>
            ) : lista.length === 0 ? (
              <div className="px-3.5 py-8 text-center">
                <Bell className="mx-auto mb-2 size-5 text-ink-3" />
                <p className="text-meta text-ink-2">Sin avisos por ahora</p>
                <p className="mt-1 text-meta text-ink-3">
                  Aquí aparecerán los pedidos nuevos.
                </p>
              </div>
            ) : (
              lista.map((aviso) => {
                const Icono = ICONOS[aviso.type] ?? Info;
                return (
                  <button
                    key={aviso.id}
                    type="button"
                    onClick={() => void abrirAviso(aviso)}
                    className={cn(
                      "flex w-full items-start gap-2.5 border-b border-line-2 px-3.5 py-3 text-left last:border-b-0 hover:bg-sunk",
                      !aviso.read_at && "bg-accent-soft/40",
                    )}
                  >
                    <Icono
                      className={cn("mt-0.5 size-4 shrink-0", TONOS[aviso.severity] ?? TONOS.info)}
                      strokeWidth={1.8}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <b className="truncate text-meta font-semibold">{aviso.title}</b>
                        <small className="shrink-0 text-mark text-ink-3">
                          {haceCuanto(aviso.created_at)}
                        </small>
                      </span>
                      {aviso.message && (
                        <span className="mt-0.5 block truncate text-meta text-ink-2">
                          {aviso.message}
                        </span>
                      )}
                    </span>
                    {!aviso.read_at && (
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
